import React, { memo, useContext, useEffect, useRef } from 'react'

import { Button, Icon, ChatAI, Close, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { iconSizeMedium } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { closeImageAssistant } from 'Pods/ThreeDStudio/Store/actions/ui'
import { IMAGE_ASSISTANT_TAB } from 'Pods/ThreeDStudio/Store/constants'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import CreateTab from './Components/CreateTab/CreateTab'
import EditTab from './Components/EditTab/EditTab'
import SearchTab from './Components/SearchTab/SearchTab'
import Tabs from './Components/Tabs/Tabs'

const closeIcon = <Icon svgComponent={Close} size={iconSizeMedium} notSpaced />

function ImageAssistantPanel() {
  const t = useTranslation()
  const {
    ideationFlowEnabled,
    imageSearchOnIdeationFLowEnabled,
  } = useContext(ThreeDStudioPageContext)
  const isOpen = useThreeDStudioStore(state => state.ui.isImageAssistantOpen)
  const activeTab = useThreeDStudioStore(state => state.ui.imageAssistantTab)

  const panelRef = useRef(null)
  const triggerRef = useRef(null)

  // Non-modal side panel: it intentionally leaves the Generate form usable, so
  // it has no focus trap. It still closes on Escape, moves focus into itself on
  // open, and restores focus to the trigger on close.
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    triggerRef.current = document.activeElement
    panelRef.current?.focus()

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeImageAssistant()
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      // Only pull focus back to the trigger if it is still inside the panel that
      // is closing, so we never yank focus away from elsewhere on the page.
      const panel = panelRef.current
      if (panel?.contains(document.activeElement)) {
        triggerRef.current?.focus?.()
      }
    }
  }, [isOpen])

  if (!ideationFlowEnabled) {
    return null
  }

  // When image search is disabled the Search tab is hidden, so fall back to
  // Create for the default/persisted Search state to keep tab highlight and
  // visible content in sync.
  const effectiveTab = !imageSearchOnIdeationFLowEnabled && activeTab === IMAGE_ASSISTANT_TAB.SEARCH
    ? IMAGE_ASSISTANT_TAB.CREATE
    : activeTab
  const isEdit = effectiveTab === IMAGE_ASSISTANT_TAB.EDIT
  const isSearch = effectiveTab === IMAGE_ASSISTANT_TAB.SEARCH
  const isCreate = effectiveTab === IMAGE_ASSISTANT_TAB.CREATE

  return (
    <div
      ref={panelRef}
      className={classnames('image-assistant-panel', isOpen && 'image-assistant-panel--opened')}
      role="dialog"
      aria-label="AI Assistant"
      aria-hidden={!isOpen}
      tabIndex={-1}
    >
      <div className="image-assistant-panel__header">
        <span className="image-assistant-panel__title cgt-heading-50">
          <Icon svgComponent={ChatAI} size={iconSizeMedium} notSpaced color="darker" />
          {t('imageAssistantPanel.title')}
        </span>
        <Button
          type="ghost"
          variant="only-icon"
          size="small"
          icon={closeIcon}
          aria-label="Close"
          onClick={closeImageAssistant}
        />
      </div>
      {/* Keying on isOpen remounts the tab content on close, resetting the
          SearchTab's internal navigation (query, selected category/image). */}
      <div className="image-assistant-panel__content" key={isOpen ? 'open' : 'closed'}>
        <div className="image-assistant-panel__intro">
          <p className="image-assistant-panel__subtitle cgt-body-base-semibold">
            {t('imageAssistantPanel.subtitle')}
          </p>
          <Tabs activeTab={effectiveTab} imageSearchOnIdeationFLowEnabled={imageSearchOnIdeationFLowEnabled} />
        </div>
        {/* All three tabs stay mounted while the panel is open so their state
            (search navigation, conversation, loaded image, mask, versions)
            survives tab switches; each is hidden (not unmounted) when another
            tab is active. They all reset on close via the __content key above. */}
        {imageSearchOnIdeationFLowEnabled && (
          <div
            className={classnames(
              'image-assistant-panel__search-host',
              !isSearch && 'image-assistant-panel__search-host--hidden',
            )}
          >
            <SearchTab />
          </div>
        )}
        <div
          className={classnames(
            'image-assistant-panel__create-host',
            !isCreate && 'image-assistant-panel__create-host--hidden',
          )}
        >
          <CreateTab />
        </div>
        <div
          className={classnames(
            'image-assistant-panel__edit-host',
            !isEdit && 'image-assistant-panel__edit-host--hidden',
          )}
        >
          <EditTab />
        </div>
      </div>
    </div>
  )
}

export default memo(ImageAssistantPanel)
